import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./styles/AssetEvaluation.module.css";
import { COUNTRIES, searchCountries } from "../../../Utils/countries";

/*
  The country field for an asset's location.

  Two hundred entries is too many to scroll, so this one takes a query — the
  trigger becomes the search box once it is open, which keeps it a single
  control rather than a field with a search box floating above it.

  Like `Dropdown`, the list is portaled into document.body: the panel it sits in
  is an `overflow: auto` container and would otherwise clip it.
*/

const MAX_H = 260;
const GAP = 4;
const MARGIN = 8;

function CountryPicker({ value, onChange, placeholder, disabled, invalid, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [box, setBox] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const selectedRef = useRef(null);
  // Whether this opening has already been scrolled to the chosen country.
  const scrolledRef = useRef(false);

  const matches = open ? searchCountries(query) : COUNTRIES;

  const place = () => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - GAP - MARGIN;
    const above = rect.top - GAP - MARGIN;
    // Above only when there is genuinely more room there.
    const up = below < 160 && above > below;
    setBox({
      left: rect.left,
      width: rect.width,
      top: up ? undefined : rect.bottom + GAP,
      bottom: up ? window.innerHeight - rect.top + GAP : undefined,
      maxHeight: Math.max(120, Math.min(MAX_H, up ? above : below)),
    });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  /*
    Reopen on the country already chosen rather than at the top of the alphabet:
    with two hundred entries, someone reviewing "United Kingdom" should not have
    to scroll the length of the list to see where they are.

    Set directly rather than with scrollIntoView, which is free to scroll the
    page as well as the menu. Once per opening, and never while a query is
    narrowing the list — the selection may not even be in the results, and
    yanking the list under someone who is typing is worse than leaving it.
  */
  useLayoutEffect(() => {
    if (!open) {
      scrolledRef.current = false;
      return;
    }
    if (scrolledRef.current || query) return;
    const menu = menuRef.current;
    const option = selectedRef.current;
    if (!menu || !option) return;
    scrolledRef.current = true;
    menu.scrollTop = Math.max(
      0,
      option.offsetTop - menu.clientHeight / 2 + option.clientHeight / 2
    );
  }, [open, box, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = () => place();
    const onDown = (event) => {
      /*
        The menu is portaled into document.body, so it is NOT inside the
        wrapper — without this, pressing an option counts as a click away: the
        menu closes on mousedown and the click never reaches it, so the country
        is never chosen and the field appears to do nothing.
      */
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        // Anything typed and not chosen is discarded, not half-applied.
        setOpen(false);
        setQuery("");
      }
    };
    window.addEventListener("resize", onScroll);
    window.addEventListener("scroll", onScroll, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const choose = (name) => {
    onChange(name);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={styles.cp_wrap} ref={wrapRef}>
      {open ? (
        <input
          ref={inputRef}
          autoFocus
          type="text"
          className={`${styles.select} ${styles.cp_search}`}
          value={query}
          placeholder={value || placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            place();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOpen(false);
              setQuery("");
            }
            // Enter takes the only match, which is what a search implies.
            if (event.key === "Enter" && matches.length === 1) {
              event.preventDefault();
              choose(matches[0]);
            }
          }}
        />
      ) : (
        <button
          type="button"
          className={`${styles.select} ${styles.cp_trigger} ${
            value ? "" : styles.select_placeholder
          } ${invalid || ""}`}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => setOpen(true)}
        >
          {value || placeholder}
        </button>
      )}

      {open &&
        box &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.cp_menu}
            role="listbox"
            aria-label={ariaLabel}
            data-dropdown-menu="true"
            style={{
              left: box.left,
              width: box.width,
              top: box.top,
              bottom: box.bottom,
              maxHeight: box.maxHeight,
            }}
          >
            {matches.length === 0 ? (
              <div className={styles.cp_empty}>—</div>
            ) : (
              matches.map((name) => (
                <div
                  key={name}
                  ref={name === value ? selectedRef : undefined}
                  role="option"
                  aria-selected={name === value}
                  className={`${styles.cp_option} ${
                    name === value ? styles.cp_option_selected : ""
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(name)}
                >
                  {name}
                </div>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default CountryPicker;
